import { useEffect, useRef, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import styles from './Hero.module.css';
import FilmGrain, { GRAIN_INTENSITY } from './FilmGrain';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatLiveTimecode(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
  const second = parts.find((p) => p.type === 'second')?.value ?? '00';
  const frames = Math.floor((date.getMilliseconds() / 1000) * 25);

  return `${hour}:${minute}:${second}:${pad2(frames)}`;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(REDUCED_MOTION_QUERY);
    setReduced(media.matches);
    const onChange = () => setReduced(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function PlayerBar() {
  const reducedMotion = usePrefersReducedMotion();
  const [timecode, setTimecode] = useState('00:00:00:00');

  useEffect(() => {
    if (reducedMotion) {
      setTimecode('00:00:00:00');
      return undefined;
    }
    const tick = () => setTimecode(formatLiveTimecode(new Date()));
    tick();
    const id = window.setInterval(tick, 40);
    return () => window.clearInterval(id);
  }, [reducedMotion]);

  return (
    <div className={styles.playerBar} aria-hidden="true">
      <span className={styles.playerTimecode}>{timecode}</span>
      <span className={styles.playerLine} />
      <span className={styles.playerRec}>
        <span className={styles.recDot} />
        REC
      </span>
    </div>
  );
}

function HeroVideo() {
  const reducedMotion = usePrefersReducedMotion();
  const [mainReady, setMainReady] = useState(false);

  // For reduced-motion users we keep the poster and skip autoplay, showing a
  // single static <video> with no motion.
  if (reducedMotion) {
    return (
      <div className={styles.videoPlaceholder}>
        <video
          className={styles.heroVideoDesktop}
          poster="/videos/hero-poster.webp"
          aria-hidden="true"
          playsInline
        >
          <source src="/videos/hero.mp4" type="video/mp4" />
        </video>
        <video
          className={styles.heroVideoMobile}
          poster="/videos/hero-poster.webp"
          aria-hidden="true"
          playsInline
        >
          <source src="/videos/hero-mobile.mp4" type="video/mp4" />
        </video>
      </div>
    );
  }

  return (
    <div className={styles.videoPlaceholder}>
      {/* Ultra-light preload clip: starts instantly, fades out once main is ready */}
      <video
        className={`${styles.heroVideoPre} ${mainReady ? styles.heroVideoPreHidden : ''}`}
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
      >
        <source src="/videos/hero-pre.mp4" type="video/mp4" />
      </video>

      {/* Main desktop video */}
      <video
        className={`${styles.heroVideoDesktop} ${mainReady ? styles.heroVideoMainIn : ''}`}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster="/videos/hero-poster.webp"
        aria-hidden="true"
        onCanPlay={() => setMainReady(true)}
        onLoadedData={() => setMainReady(true)}
      >
        <source src="/videos/hero.mp4" type="video/mp4" />
      </video>

      {/* Main mobile video */}
      <video
        className={`${styles.heroVideoMobile} ${mainReady ? styles.heroVideoMainIn : ''}`}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster="/videos/hero-poster.webp"
        aria-hidden="true"
        onCanPlay={() => setMainReady(true)}
        onLoadedData={() => setMainReady(true)}
      >
        <source src="/videos/hero-mobile.mp4" type="video/mp4" />
      </video>
    </div>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  const { tr, t } = useLang();

  return (
    <section ref={sectionRef} className={styles.hero} aria-label={tr('Hero', 'Hero')}>
      <div className={styles.videoWrap}>
        <HeroVideo />
        <div className={styles.overlay} aria-hidden="true" />
        <div className={styles.aurora} aria-hidden="true" />
        <div className={styles.viewfinder} aria-hidden="true">
          <span className={styles.corner} data-pos="tl" />
          <span className={styles.corner} data-pos="tr" />
          <span className={styles.corner} data-pos="bl" />
          <span className={styles.corner} data-pos="br" />
        </div>
      </div>

      {/* Lower-third name title */}
      <div className={styles.lowerThird}>
        <span className={styles.lowerBar} aria-hidden="true" />
        <div className={styles.lowerText}>
          <h1 className={styles.tagline}>
            {tr(
              <>Your event is amazing.<br className={styles.mobileBreak} /> Show it.</>,
              <>Ihr Event ist großartig.<br className={styles.mobileBreak} /> Zeigen Sie es.</>,
            )}
          </h1>
        </div>
        <a href="#contact" className={`btn-gradient ${styles.cta}`}>
          {tr('Start a project', 'Projekt starten')}
        </a>
      </div>

      {/* Bottom player bar */}
      <PlayerBar />

      {/* Scroll cue */}
      <a href="#work" className={styles.scrollCue} aria-label={t('hero.scrollAria')}>
        <span className={styles.scrollText}>{t('hero.scroll')}</span>
        <span className={styles.scrollLine} aria-hidden="true" />
      </a>

      <FilmGrain intensity={GRAIN_INTENSITY.whisper} sectionRef={sectionRef} />
    </section>
  );
}
